import { useCallback, useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { IconButton } from '@material-ui/core';
import LockAvatar from '../LockAvatar';
import { InputV2 } from '../../../subComponents/input';
import { ButtonV2 } from '../../../subComponents/button';
import { POPUP_TYPE } from '../../../subComponents/popup';
import { RecoverPasswordContext } from '../../contexts';
import { MODAL_KEY, IPasswordMode } from '../../constant';
import { CommonPortalContext } from '../../../../contexts/commonPortal';
import { IPopup } from '../../../../constants/commonPortal.constant';

export const UserNameInputForm = () => {
  const {
    recoverForm,
    setRecoverForm,

    modalCollection,

    recoverPassword,

    handler: {
      handleSendOTP,
    },
  } = useContext(RecoverPasswordContext);

  const disabled = useMemo(() => {
    if (modalCollection.has(MODAL_KEY.CONFIRM_OTP)) {
      return true;
    }

    return recoverForm.username.length < 3;
  }, [recoverForm, modalCollection]);

  const loading = useMemo(() => {
    if (modalCollection.has(MODAL_KEY.CONFIRM_OTP)) {
      return false;
    }

    return recoverPassword.sendOTP.loading;
  }, [recoverPassword, modalCollection]);

  const allowNavigation = useMemo(() => {
    if (!modalCollection.has(MODAL_KEY.CONFIRM_OTP)) {
      return true;
    }

    return false;
  }, [modalCollection]);

  const handleUsernameChange = ({ target }) => {
    setRecoverForm({ ...recoverForm, username: target.value });
  };

  return (
    <div className='flex flex-column gap-16 width-360 max-width-360 min-height-520'>
      <div className='flex align-items-center justify-content-center'>
        <LockAvatar />
      </div>

      <div className='flex align-items-center justify-content-center font-24'>
        Reset Password
      </div>

      <InputV2
        name='username'
        label='Username *'
        value={recoverForm.username}
        onChange={handleUsernameChange}
        autoFocus
        message=''
      />

      <div className='flex font-12 line-18'>
        If you forgot your username, please contact the HCP Operations Team. Clicking the "Reset Password" button will send a verification code to the phone number associated with your account.
      </div>

      <ButtonV2
        label='Reset Password'
        disabled={disabled}
        loading={loading}
        onClick={handleSendOTP}
      />

      {
        allowNavigation && (
          <div className='flex justify-content-center'>
            <Link to='/' className='color-primary-v2 font-14'>Go back to Sign In</Link>
          </div>
        )
      }
    </div>
  );
};

export const PasswordInputForm = () => {
  const {
    passwordForm,
    setPasswordForm,

    visibilityOption,
    setVisibilityOption,

    history,

    recoverPassword: {
      resetUsingOTP,
    },

    action: {
      recoverPassword = async () => ({}),
    },
  } = useContext(RecoverPasswordContext);

  const { handler: { handleSnackbarClose, handleSnackbarOpen } } = useContext(CommonPortalContext);

  const handleInputKeyDown = (event) => {
    const expressions = [
      /[A-Z]/gm,
      /[a-z]/gm,
      /[0-9]/gm,
      /(?=.*[!@#$%^*-])/gm,
      /(Backspace|Delete|Shift|Tab|ArrowLeft|ArrowRight)/gm,
    ];
    const results = expressions.map((expression) => expression.test(event.key));
    const foundIndex = results.findIndex((result) => result);
    if (foundIndex === -1) {
      return event.preventDefault();
    }
    
    return {};
  };

  const handleInputChange = ({ target }) => {
    setPasswordForm({
      ...passwordForm,
      [target.name]: target.value,
    });
  };

  const handleResetPassword = async (event) => {
    try {
      const { data, status } = await recoverPassword(passwordForm);
      if (status === 200) {
        handleSnackbarOpen(IPopup({
          open: true,
          title: 'Successful',
          type: POPUP_TYPE.SUCCESS,
          message: 'Password is updated successfully!',
          onOk: handleSnackbarClose,
        }));

        history.push('/');
      }

      if (status !== 200) {
        const {
          Error: message = 'Could not reset password, please try again.',
          Status: title = 'Error',
        } = data;

        handleSnackbarOpen(IPopup({
          open: true,
          title,
          type: POPUP_TYPE.ERROR,
          message,
          onOk: handleSnackbarClose,
        }));
      }
    } catch (e) {
      
    }
  };

  const handleToggleType = ({ key, type }) => {
    const collection = new Map(visibilityOption);
    collection.set(key, type);
    setVisibilityOption(collection);
  };

  const prepareVisibility = useCallback((field = '') => {
    const passwordMode = IPasswordMode();
    const passwordOption = visibilityOption.get(field);
    return passwordMode.get(passwordOption);
  }, [visibilityOption])

  const passwordVisibility = useMemo(() => prepareVisibility('password'), [prepareVisibility]);

  const confirmPasswordVisibility = useMemo(() => prepareVisibility('passwordConfirm'), [prepareVisibility]);

  const passwordError = useMemo(() => {
    const _error = { has: false, message: '' };

    if (passwordForm.password.length === 0) {
      return _error;
    }

    if (passwordForm.password.length < 8) {
      _error.has = true;
      _error.message = 'Password must be 8 characters long';
      return _error;
    }

    const regexA = /[A-Z]/gm;
    const regexB = /[a-z]/gm;
    const regexC = /[0-9]/gm;
    const regexD = /(?=.*[!@#$%^*-])/gm;

    if (!regexA.test(passwordForm.password)) {
      _error.has = true;
      _error.message = 'Missing uppercase letter';
      return _error;
    }

    if (!regexB.test(passwordForm.password)) {
      _error.has = true;
      _error.message = 'Missing lowercase letter';
      return _error;
    }

    if (!regexC.test(passwordForm.password)) {
      _error.has = true;
      _error.message = 'Missing number';
      return _error;
    }

    if (!regexD.test(passwordForm.password)) {
      _error.has = true;
      _error.message = 'Missing special character';
      return _error;
    }

    return _error;
  }, [passwordForm]);

  const confirmPasswordError = useMemo(() => {
    const _error = { has: false, message: '' };

    if (passwordForm.password.length === 0 && passwordForm.passwordConfirm.length === 0) {
      return _error;
    }

    if (passwordForm.password !== passwordForm.passwordConfirm) {
      _error.has = true;
      _error.message = 'Confirm password do not match';
      return _error;
    }

    return _error;
  }, [passwordForm]);

  const disabled = useMemo(() => {
    if (resetUsingOTP.loading) {
      return true;
    }

    if (passwordForm.password.length === 0) {
      return true;
    }

    if (passwordError.has) {
      return true;
    }

    if (confirmPasswordError.has) {
      return true;
    }

    return resetUsingOTP.loading;
  }, [passwordError, confirmPasswordError, passwordForm, resetUsingOTP]);

  return (
    <div className='flex flex-column gap-16 width-360 max-width-360 min-height-520'>
      <div className='flex align-items-center justify-content-center'>
        <LockAvatar />
      </div>

      <div className='flex align-items-center justify-content-center font-24'>
        Update Password
      </div>

      <InputV2
        name='password'
        label='Password *'
        type={passwordVisibility.type}
        value={passwordForm.password}
        onChange={handleInputChange}
        onKeyDown={handleInputKeyDown}
        autoFocus
        error={passwordError.has}
        message={passwordError.message}
        InputProps={{
          endAdornment: (
            <IconButton size='small' onClick={handleToggleType.bind(this, { key: 'password', type: passwordVisibility.nextType })}>
              <passwordVisibility.Icon fontSize='small' />
            </IconButton>
          )
        }}
      />

      <InputV2
        name='passwordConfirm'
        label='Confirm Password *'
        type={confirmPasswordVisibility.type}
        value={passwordForm.passwordConfirm}
        onChange={handleInputChange}
        onKeyDown={handleInputKeyDown}
        error={confirmPasswordError.has}
        message={confirmPasswordError.message}
        InputProps={{
          endAdornment: (
            <IconButton size='small' onClick={handleToggleType.bind(this, { key: 'passwordConfirm', type: confirmPasswordVisibility.nextType })}>
              <confirmPasswordVisibility.Icon fontSize='small' />
            </IconButton>
          )
        }}
      />

      <div className='flex font-14 line-20'>
        <ul className='p-l-4 m-t-0 m-b-0'>
          <li>Minimum 8 characters long</li>
          <li>1 uppercase letter</li>
          <li>1 lowercase letter</li>
          <li>1 of the following special characters ! @ # $ % ^ * -</li>
          <li>And a number</li>
        </ul>
      </div>

      <ButtonV2
        label='Update Password'
        disabled={disabled}
        loading={resetUsingOTP.loading}
        onClick={handleResetPassword}
      />
    </div>
  );
};

export default UserNameInputForm;
